// Servicio de Gestión de Datos (Data Management)
// Permite limpiar, exportar e importar datos de colecciones

import { 
  collection, 
  getDocs, 
  doc, 
  writeBatch, 
  Timestamp
} from 'firebase/firestore'
import { db } from './firebase.config'

/**
 * Limpiar todos los documentos de una colección
 * @param {string} collectionName - Nombre de la colección
 * @returns {Promise<number>} Número de documentos eliminados
 */
export const clearCollection = async (collectionName) => {
  try {
    const q = collection(db, collectionName)
    const snapshot = await getDocs(q)
    
    if (snapshot.empty) return 0

    const batchSize = 500
    let batch = writeBatch(db)
    let count = 0
    let totalDeleted = 0

    for (const document of snapshot.docs) {
      batch.delete(doc(db, collectionName, document.id))
      count++

      if (count >= batchSize) {
        await batch.commit()
        totalDeleted += count
        batch = writeBatch(db)
        count = 0
      }
    }

    if (count > 0) {
      await batch.commit()
      totalDeleted += count
    }

    return totalDeleted
  } catch (error) {
    console.error(`Error limpiando colección ${collectionName}:`, error)
    throw error
  }
}

/**
 * Exportar datos de múltiples colecciones
 * @param {Array<string>} collectionNames - Nombres de las colecciones a exportar
 * @returns {Promise<Object>} Objeto con los datos de todas las colecciones
 */
export const exportData = async (collectionNames) => {
  try {
    const data = {}

    for (const name of collectionNames) {
      const q = collection(db, name)
      const snapshot = await getDocs(q)
      
      data[name] = snapshot.docs.map(doc => {
        const docData = doc.data()
        // Convertir Timestamps a formato serializable
        const serializableData = Object.entries(docData).reduce((acc, [key, value]) => {
          if (value && typeof value.toMillis === 'function') {
            acc[key] = { _type: 'timestamp', seconds: value.seconds, nanoseconds: value.nanoseconds }
          } else if (value && typeof value === 'object' && !Array.isArray(value)) {
             // Recursivamente buscar timestamps en objetos anidados (simple nivel)
             // Para una solución más robusta se requeriría una función recursiva profunda
             acc[key] = value // Simplificación por ahora
          } else {
            acc[key] = value
          }
          return acc
        }, {})

        return {
          id: doc.id,
          ...serializableData
        }
      })
    }

    return {
      metadata: {
        exportedAt: new Date().toISOString(),
        version: '1.0'
      },
      data
    }
  } catch (error) {
    console.error('Error exportando datos:', error)
    throw error
  }
}

/**
 * Importar datos a colecciones
 * @param {Object} jsonData - Datos a importar (estructura generada por exportData)
 * @param {boolean} clearBeforeImport - Si true, limpia la colección antes de importar
 * @returns {Promise<Object>} Resumen de la importación
 */
export const importData = async (jsonData, clearBeforeImport = false) => {
  try {
    const collections = jsonData.data || jsonData // Soporte para estructura con o sin metadata
    const results = {}

    for (const [collectionName, documents] of Object.entries(collections)) {
      if (collectionName === 'metadata') continue
      if (!Array.isArray(documents)) continue

      // Limpiar colección si se solicita
      if (clearBeforeImport) {
        await clearCollection(collectionName)
      }

      const batchSize = 500
      let batch = writeBatch(db)
      let count = 0
      let totalImported = 0

      for (const docData of documents) {
        const { id, ...data } = docData
        
        // Reconstruir Timestamps
        const processedData = Object.entries(data).reduce((acc, [key, value]) => {
          if (value && typeof value === 'object' && value._type === 'timestamp') {
            acc[key] = new Timestamp(value.seconds, value.nanoseconds)
          } else {
            acc[key] = value
          }
          return acc
        }, {})

        // Usar setDoc para preservar el ID o crear uno nuevo si no existe
        const docRef = id ? doc(db, collectionName, id) : doc(collection(db, collectionName))
        batch.set(docRef, processedData)
        count++

        if (count >= batchSize) {
          await batch.commit()
          totalImported += count
          batch = writeBatch(db)
          count = 0
        }
      }

      if (count > 0) {
        await batch.commit()
        totalImported += count
      }

      results[collectionName] = totalImported
    }

    return results
  } catch (error) {
    console.error('Error importando datos:', error)
    throw error
  }
}

export const migrateLegacyProductData = async () => {
  try {
    const q = collection(db, 'products')
    const snapshot = await getDocs(q)
    if (snapshot.empty) return 0

    let batch = writeBatch(db)
    let count = 0
    let migrated = 0

    for (const docSnap of snapshot.docs) {
      const product = { id: docSnap.id, ...docSnap.data() }
      const hasVariants = Array.isArray(product.variants) && product.variants.length > 0
      const hasLegacyImages = Array.isArray(product.images) && product.images.length > 0
      const hasLegacyIdentifiers = !!(product.sku || product.serialNumber)
      const hasLegacySelection = !!(product.color || product.size || product.material)
      const legacyVariantId = `legacy-${product.id}`
      const hasLegacyVariant = hasVariants && product.variants.some(variant => variant?.id === legacyVariantId)

      if ((hasLegacyImages || hasLegacyIdentifiers || hasLegacySelection) && !hasLegacyVariant) {
        const newVariant = {
          id: legacyVariantId,
          type: product.variantType || 'legacy',
          name: 'Variante Legado',
          sku: product.sku || '',
          serialNumber: product.serialNumber || '',
          images: Array.isArray(product.images) ? product.images : [],
          videos: Array.isArray(product.videos) ? product.videos : [],
          price: product.price ?? 0,
          stock: product.stock ?? 0,
          selection: {
            color: product.color || '',
            size: product.size || '',
            material: product.material || ''
          },
          createdAt: new Date().toISOString()
        }

        const productRef = doc(db, 'products', product.id)
        const nextVariants = Array.isArray(product.variants) ? [...product.variants, newVariant] : [newVariant]
        batch.update(productRef, {
          images: [],
          sku: '',
          serialNumber: '',
          color: '',
          size: '',
          material: '',
          price: product.price ?? 0,
          stock: 0,
          variants: nextVariants
        })
        count++
        migrated++
      }

      if (count >= 400) {
        await batch.commit()
        batch = writeBatch(db)
        count = 0
      }
    }

    if (count > 0) {
      await batch.commit()
    }
    return migrated
  } catch (error) {
    console.error('Error migrando datos legacy:', error)
    throw error
  }
}
